import { useEffect, RefObject } from 'react';

interface UseScrollWithBoundaryOptions {
  scrollRef: RefObject<HTMLElement | null>;
  containerRef?: RefObject<HTMLElement | null>;
  fallbackScrollRef?: RefObject<HTMLElement | null>;
  preferFallbackFirst?: boolean;
  enabled?: boolean;
  scrollTargetKey?: string | number;
}

export function useScrollWithBoundary({
  scrollRef,
  containerRef,
  fallbackScrollRef,
  preferFallbackFirst = false,
  enabled = true,
  scrollTargetKey,
}: UseScrollWithBoundaryOptions) {
  useEffect(() => {
    const container = containerRef?.current ?? scrollRef.current;
    const scrollElement = scrollRef.current;
    if (!container || !scrollElement || !enabled) return;

    let fallbackDeltaAccum = 0;
    let fallbackRafId: number | null = null;

    const flushFallbackScroll = () => {
      fallbackRafId = null;
      const fallback = fallbackScrollRef?.current;
      if (!fallback || fallbackDeltaAccum === 0) return;

      const next = Math.round(fallback.scrollTop + fallbackDeltaAccum);
      fallback.scrollTop = Math.max(
        0,
        Math.min(next, fallback.scrollHeight - fallback.clientHeight),
      );
      fallbackDeltaAccum = 0;
    };

    const scheduleFallbackScroll = (deltaY: number) => {
      fallbackDeltaAccum += deltaY;

      if (fallbackRafId === null) {
        fallbackRafId = requestAnimationFrame(flushFallbackScroll);
      }
    };

    let scrollingFallbackDown = false;
    let scrollingFallbackUp = false;

    const handleWheel = (e: WheelEvent) => {
      const fallback = fallbackScrollRef?.current;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      if (preferFallbackFirst && fallback) {
        const fallbackMax = fallback.scrollHeight - fallback.clientHeight;
        const canFallbackScrollDown =
          isScrollingDown && fallback.scrollTop < fallbackMax - 2;
        const canFallbackScrollUp = isScrollingUp && fallback.scrollTop > 2;

        if (canFallbackScrollDown || canFallbackScrollUp) {
          e.preventDefault();
          scheduleFallbackScroll(e.deltaY);
          return;
        }

        const hasOverflow =
          scrollElement.scrollHeight > scrollElement.clientHeight;
        if (!hasOverflow) {
          e.preventDefault();
          scheduleFallbackScroll(e.deltaY);
          return;
        }

        const scrollTop = scrollElement.scrollTop;
        const maxScroll =
          scrollElement.scrollHeight - scrollElement.clientHeight;
        const isAtBottom = scrollTop >= maxScroll - 2;
        const isAtTop = scrollTop <= 2;

        if ((isScrollingDown && isAtBottom) || (isScrollingUp && isAtTop)) {
          e.preventDefault();
          scheduleFallbackScroll(e.deltaY);
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        scrollElement.scrollTop = Math.round(
          scrollElement.scrollTop + e.deltaY,
        );
        return;
      }

      const hasOverflow =
        scrollElement.scrollHeight > scrollElement.clientHeight;

      if (!hasOverflow) {
        if (fallback) {
          e.preventDefault();
          scheduleFallbackScroll(e.deltaY);
        }
        return;
      }

      const scrollTop = scrollElement.scrollTop;
      const maxScroll = scrollElement.scrollHeight - scrollElement.clientHeight;
      const isAtBottom = scrollTop >= maxScroll - 2;
      const isAtTop = scrollTop <= 2;

      if (scrollingFallbackDown) {
        if (isScrollingDown && fallback) {
          e.preventDefault();
          scheduleFallbackScroll(e.deltaY);
          return;
        }
        scrollingFallbackDown = false;
      }
      if (scrollingFallbackUp) {
        if (isScrollingUp && fallback) {
          e.preventDefault();
          scheduleFallbackScroll(e.deltaY);
          return;
        }
        scrollingFallbackUp = false;
      }

      if (isScrollingDown && isAtBottom && fallback) {
        e.preventDefault();
        scrollingFallbackDown = true;
        scheduleFallbackScroll(e.deltaY);
        return;
      }

      if (isScrollingUp && isAtTop && fallback) {
        e.preventDefault();
        scrollingFallbackUp = true;
        scheduleFallbackScroll(e.deltaY);
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      scrollElement.scrollTop = Math.round(scrollElement.scrollTop + e.deltaY);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (fallbackRafId !== null) cancelAnimationFrame(fallbackRafId);
    };
  }, [
    scrollRef,
    containerRef,
    fallbackScrollRef,
    preferFallbackFirst,
    enabled,
    scrollTargetKey,
  ]);
}
